import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ModalSlotId } from '@/type';

/**
 * Single source of truth for auto-popup modal priority. Higher number wins when several modals
 * want the screen at once; gaps are left for future inserts. Module-private on purpose — callers
 * reference slots by `ModalSlotId`, never by number, so the order can't drift across the app.
 */
const MODAL_PRIORITY: Record<ModalSlotId, number> = {
  [ModalSlotId.Cookie]: 100,
  [ModalSlotId.PremiumExpired]: 80,
  [ModalSlotId.AgbChanged]: 70,
  [ModalSlotId.SubscribeNewsletters]: 60,
  [ModalSlotId.NewsletterConsent]: 50,
  [ModalSlotId.DailyTipper]: 20,
  [ModalSlotId.AddIcon]: 10,
};

type WantsMap = Partial<Record<ModalSlotId, boolean>>;

interface IModalRegistry {
  setWant: (id: ModalSlotId, wantsToShow: boolean) => void;
  clearWant: (id: ModalSlotId) => void;
}

// Two contexts on purpose: the registry (stable setters) and the derived winner (changes often).
// Keeping them apart means `useModalSlot`'s register/cleanup effects depend only on the stable
// registry — they never re-run (and never wrongly release the slot) when `activeSlot` changes.
const ModalRegistryContext = React.createContext<IModalRegistry | undefined>(undefined);
const ModalActiveSlotContext = React.createContext<ModalSlotId | null>(null);

export const ModalCoordinatorProvider: React.FC = ({ children }) => {
  const [wants, setWants] = useState<WantsMap>({});

  const setWant = useCallback((id: ModalSlotId, wantsToShow: boolean) => {
    setWants((prev) => (prev[id] === wantsToShow ? prev : { ...prev, [id]: wantsToShow }));
  }, []);

  const clearWant = useCallback((id: ModalSlotId) => {
    setWants((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const registry = useMemo<IModalRegistry>(() => ({ setWant, clearWant }), [setWant, clearWant]);

  const activeSlot = useMemo<ModalSlotId | null>(() => {
    let winner: ModalSlotId | null = null;
    let winnerPriority = -Infinity;
    (Object.keys(wants) as ModalSlotId[]).forEach((id) => {
      if (wants[id] && MODAL_PRIORITY[id] > winnerPriority) {
        winner = id;
        winnerPriority = MODAL_PRIORITY[id];
      }
    });
    return winner;
  }, [wants]);

  return (
    <ModalRegistryContext.Provider value={registry}>
      <ModalActiveSlotContext.Provider value={activeSlot}>
        {children}
      </ModalActiveSlotContext.Provider>
    </ModalRegistryContext.Provider>
  );
};

/**
 * An auto-popup modal declares its intent to show; returns whether it's the current winner.
 * Render the modal only when `isActive`. Drive `wantsToShow` off the modal's local open state so
 * dismissing releases the slot immediately (the next modal shows without waiting on the network).
 *
 * Provider-tolerant: with no coordinator above (e.g. a modal reused on a public route) the effects
 * no-op and `isActive` defaults to `true`, so the modal shows normally.
 */
// eslint-disable-next-line
export const useModalSlot = (id: ModalSlotId, wantsToShow: boolean): { isActive: boolean } => {
  const registry = useContext(ModalRegistryContext);
  const activeSlot = useContext(ModalActiveSlotContext);

  useEffect(() => {
    registry?.setWant(id, wantsToShow);
  }, [registry, id, wantsToShow]);

  useEffect(() => {
    if (!registry) return;
    return () => registry.clearWant(id);
  }, [registry, id]);

  return { isActive: registry ? activeSlot === id : true };
};
